import { useMemo, useCallback, useState } from 'react';
import axios from 'axios';
import { useAppSelector, useMainTimePeriod, useSnackbar } from 'src/utils/Hooks/typedHooks';
import { getQueryParamValue } from 'src/utils/app';
import { useQuery } from '@tanstack/react-query';
import {
  useUpdateContentLibrary,
  ContentLibraryAction,
  useExportContent,
  useGetContentLibrary
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { useComparisonMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useComparisonMetrics';
import {
  apiCompareProductKeywords,
  apiGetCurrentContent,
  apiGetContentGenerationStatus
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/contentGenApis';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import {
  ContentLibraryManagementRequest,
  MasterContentRecord,
  KeywordSection,
  ContentLibraryRecord,
  ProductKeywordsComparisonRequest,
  ProductKeywordsPayload
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { parseAndMergeNewContent } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/utils';

import {
  buildKeywordsPayload,
  formatDateForDisplay,
  formatKeywordSections
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/reviewUtils';

export const useLibraryReview = () => {
  const { showSnackbar } = useSnackbar();
  const { beaconNavigator } = useBeaconNavigator();

  const retailerId = getQueryParamValue('rid', '');
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const { endWeek } = useMainTimePeriod();
  const users = useAppSelector((state) => state.user.team);

  // Get data from URL params
  const contentGenId = getQueryParamValue('contentGenId', '');
  const stacklineSku = getQueryParamValue('sku', '');
  const retailerSku = getQueryParamValue('sku', '');
  const profileId = getQueryParamValue('profileId', '');

  // State for selected versions
  const [leftSelectedVersion, setLeftSelectedVersion] = useState<string>('master');
  const [rightSelectedVersion, setRightSelectedVersion] = useState<string>(contentGenId || 'current');

  // Fetch content library to get all versions
  const { data: libraryData, isLoading: isLoadingLibrary } = useGetContentLibrary({
    retailerId: Number(retailerId),
    retailerSkus: [retailerSku],
    filterNullLibraryStatus: true
  });

  // Get the library record for this SKU
  const libraryRecord = useMemo<ContentLibraryRecord | null>(() => {
    if (!libraryData?.records || libraryData.records.length === 0) return null;
    return libraryData.records.find((record) => record.retailerSku === retailerSku) || libraryData.records[0];
  }, [libraryData, retailerSku]);

  // Collect userIds referenced by versions for fetching user profiles
  const versionUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (libraryRecord?.versions && libraryRecord.versions.length > 0) {
      libraryRecord.versions.forEach((version) => {
        ids.add(version.createdBy);
      });
    }
    return Array.from(ids);
  }, [libraryRecord]);

  // Fetch user profile metadata for display names, similar to useLibrary.ts
  const { data: userMetadata } = useQuery({
    queryKey: ['userMetadata', versionUserIds],
    queryFn: async () => {
      if (!versionUserIds || versionUserIds.length === 0) return {};
      const response = await axios.post('/api/user/GetUserProfilesFromUserIds', versionUserIds);
      return response.data as Record<
        string,
        { firstName?: string; lastName?: string; email?: string; role?: string }
      >;
    },
    enabled: (versionUserIds?.length || 0) > 0,
    staleTime: 5 * 60 * 1000
  });

  // Build version list for dropdowns
  const allVersionOptions = useMemo(() => {
    const options: Array<{
      id: string;
      label: string;
      createdBy: {
        userId: string;
        displayName: string;
        role: string;
        imageSrc: string;
      };
      updatedAt?: string;
    }> = [];

    // Add versions first
    if (libraryRecord?.versions && libraryRecord.versions.length > 0) {
      libraryRecord.versions.forEach((version, index) => {
        const versionId = version.skuContentGenerationId;
        const resolvedUserId = version.createdBy || version.updatedBy || '';
        const meta = userMetadata?.[resolvedUserId];
        const metaDisplayName =
          resolvedUserId === 'content-generation-workflow'
            ? 'Stackline'
            : meta?.firstName && meta?.lastName
            ? `${meta.firstName} ${meta.lastName}`
            : undefined;
        const role = (meta?.role as string | undefined) || (users?.find((u: any) => u.id === resolvedUserId)?.role as string | undefined);
        options.push({
          id: versionId,
          label: `Version ${libraryRecord.versions.length - index}`,
          createdBy: {
            userId: resolvedUserId || 'content-generation-workflow',
            displayName: metaDisplayName || 'Stackline',
            role: role || 'support',
            imageSrc: ''
          },
          updatedAt: version.updatedAt
        });
      });
    }

    // Add master second to last
    if (libraryRecord?.masterContent) {
      const masterUpdatedAt = libraryRecord.masterContent.updatedAt || 
        (libraryRecord.masterContent.timestamp ? new Date(libraryRecord.masterContent.timestamp).toISOString() : undefined);
      options.push({
        id: 'master',
        label: 'Uploaded Content',
        createdBy: {
          userId: 'content-generation-workflow',
          displayName: 'Stackline',
          role: 'support',
          imageSrc: ''
        },
        updatedAt: masterUpdatedAt
      });
    }

    // Add current last
    const currentUpdatedAt = libraryRecord?.currentContent?.updatedAt || 
      (libraryRecord?.currentContent?.timestamp ? new Date(libraryRecord.currentContent.timestamp).toISOString() : undefined);
    options.push({
      id: 'current',
      label: 'Current Content',
      createdBy: {
        userId: 'content-generation-workflow',
        displayName: 'Stackline',
        role: 'support',
        imageSrc: ''
      },
      updatedAt: currentUpdatedAt
    });

    return options;
  }, [libraryRecord, users, userMetadata]);

  const leftComparisonOptions = allVersionOptions;
  const availableVersions = allVersionOptions;

  const buildPayload = useCallback(
    (params: {
      title?: string;
      description?: string;
      bullets?: string[];
      skuContentGenerationId?: string | undefined;
    }): ProductKeywordsPayload => {
      return buildKeywordsPayload(
        { retailerId: Number(retailerId), retailerSku: stacklineSku, weekId: endWeek },
        {
          title: params.title,
          description: params.description,
          bullets: params.bullets,
          skuContentGenerationId: params.skuContentGenerationId
        }
      );
    },
    [retailerId, stacklineSku, endWeek]
  );

  // Memoize the query key
  const libraryReviewQueryKey = useMemo(
    () => ['LIBRARY_REVIEW_COMPARE', stacklineSku, retailerId, endWeek, leftSelectedVersion, rightSelectedVersion] as const,
    [stacklineSku, retailerId, endWeek, leftSelectedVersion, rightSelectedVersion]
  );

  const {
    data: comparisonSections,
    isLoading: isLoadingCompare,
    error
  } = useQuery({
    queryKey: libraryReviewQueryKey,
    queryFn: async () => {
      // Helper to resolve selection into content and sku id
      const resolveSelection = async (
        selectionId: string
      ): Promise<{
        title?: string;
        description?: string;
        bullets?: string[];
        skuId?: string | undefined;
        timestamp?: number;
      }> => {
        if (selectionId === 'current') {
          const res = await apiGetCurrentContent({
            retailerSku: stacklineSku,
            retailerId: Number(retailerId),
            weekId: endWeek,
            skuContentGenerationId: ''
          });
          return {
            title: res.data?.title,
            description: res.data?.description,
            bullets: res.data?.bullets,
            skuId: undefined,
            timestamp: res.data?.timestamp
          };
        }

        if (selectionId === 'master') {
          if (!libraryRecord?.masterContent) throw new Error('Master content not available');
          const master = libraryRecord.masterContent as MasterContentRecord;

          return {
            title: master.title,
            description: master.description,
            bullets: master.bullets || [],
            skuId: undefined
          };
        }

        // Generated version
        const statusRes = await apiGetContentGenerationStatus({
          beaconClientId: beaconClientId || 0,
          skuContentGenerationIds: [selectionId]
        });
        const item = statusRes.data?.[selectionId];
        if (!item) throw new Error('Generated content not found');
        const parsed = parseAndMergeNewContent(item);
        return {
          title: parsed.content?.title,
          description: parsed.content?.description,
          bullets: parsed.content?.featureBullets || [],
          skuId: selectionId
        };
      };

      const [leftResolved, rightResolved] = await Promise.all([
        resolveSelection(leftSelectedVersion),
        resolveSelection(rightSelectedVersion)
      ]);

      const currentPayload: ProductKeywordsPayload = buildPayload({
        title: leftResolved.title,
        description: leftResolved.description,
        bullets: leftResolved.bullets,
        skuContentGenerationId: leftResolved.skuId
      });

      const newPayload: ProductKeywordsPayload = buildPayload({
        title: rightResolved.title,
        description: rightResolved.description,
        bullets: rightResolved.bullets,
        skuContentGenerationId: rightResolved.skuId
      });

      const body: ProductKeywordsComparisonRequest = {
        current: currentPayload,
        new: newPayload
      };

      const compareRes = await apiCompareProductKeywords(body);
      const currentSections = compareRes.data?.data?.current_analysis?.sections as KeywordSection[] | undefined;
      const newSections = compareRes.data?.data?.new_analysis?.sections as KeywordSection[] | undefined;

      return {
        left: formatKeywordSections(currentSections),
        leftResolved: leftResolved,
        right: formatKeywordSections(newSections),
        rightResolved: rightResolved
      };
    },
    enabled:
      !!stacklineSku &&
      !!retailerId &&
      !!endWeek &&
      !!leftSelectedVersion &&
      !!rightSelectedVersion &&
      !!(leftSelectedVersion !== 'master' || libraryRecord?.masterContent) &&
      !!(rightSelectedVersion !== 'master' || libraryRecord?.masterContent) &&
      (leftSelectedVersion === 'current' || leftSelectedVersion === 'master' || !!beaconClientId) &&
      (rightSelectedVersion === 'current' || rightSelectedVersion === 'master' || !!beaconClientId),
    staleTime: 0, // Allow immediate refetch when invalidated
    refetchOnWindowFocus: false
  });

  // Get sections for each side
  const leftSections = useMemo<KeywordSection[]>(() => {
    return (comparisonSections?.left as KeywordSection[]) || [];
  }, [comparisonSections]);

  const rightSections = useMemo<KeywordSection[]>(() => {
    return (comparisonSections?.right as KeywordSection[]) || [];
  }, [comparisonSections]);

  const isLoadingLeft = isLoadingCompare || isLoadingLibrary;
  const isLoadingRight = isLoadingCompare || isLoadingLibrary;

  // Fetch raw content generation item to get profileVersion
  const { data: contentGenItemData } = useQuery({
    queryKey: ['ContentGenerationItem', contentGenId],
    queryFn: async () => {
      if (!beaconClientId || !contentGenId) return null;
      const response = await apiGetContentGenerationStatus({
        beaconClientId,
        skuContentGenerationIds: [contentGenId]
      });
      return response.data[contentGenId];
    },
    enabled: !!beaconClientId && !!contentGenId
  });

  const profileVersion = contentGenItemData?.profileVersion;

  // Fetch profile data
  const { profile } = useGetSingleProfile(profileId || undefined);

  // Library actions
  const { updateContentLibrary: saveAsDraft, isPending: isSavingDraft } = useUpdateContentLibrary('draft');
  const { updateContentLibrary: saveAsMaster, isPending: isSavingMaster } = useUpdateContentLibrary('master');
  const { updateContentLibrary: archiveContent, isPending: isArchiving } = useUpdateContentLibrary('archive');
  const { exportContent, isPending: isExporting } = useExportContent();

  const isSaving = isSavingDraft || isSavingMaster || isArchiving;

  // Only fetch metrics AFTER comparison data is loaded (both sides)
  const canFetchMetrics = !isLoadingLeft && !isLoadingRight;
  const canFetchNewMetrics = canFetchMetrics && !!comparisonSections?.rightResolved;

  const profileVersionInput = profileVersion;

  const { leftMetrics, rightMetrics, metricLoadingStates } = useComparisonMetrics({
    retailerSku: stacklineSku,
    retailerId: Number(retailerId),
    weekId: endWeek,
    stacklineSku,
    profileId: profileId || undefined,
    profileVersion: profileVersionInput,
    leftResolved: {
      title: comparisonSections?.leftResolved?.title,
      description: comparisonSections?.leftResolved?.description,
      bullets: comparisonSections?.leftResolved?.bullets
    },
    rightResolved: comparisonSections?.rightResolved
      ? {
          title: comparisonSections?.rightResolved?.title,
          description: comparisonSections?.rightResolved?.description,
          bullets: comparisonSections?.rightResolved?.bullets
        }
      : undefined,
    leftSections,
    rightSections,
    enabledLeft: canFetchMetrics,
    enabledRight: canFetchNewMetrics
  });

  // Handlers
  const handleBack = useCallback(() => {
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.GENERATION,
      subtab: BEACON_SUBTABS.LIBRARY
    });
    beaconNavigator.go();
  }, [beaconNavigator]);

  const handleUpdateContentLibrary = useCallback(
    (type: ContentLibraryAction) => {
      const contentLibraryManagementRequest: ContentLibraryManagementRequest = {
        skuContentGenerationId: contentGenId,
        retailerId: Number(retailerId),
        retailerSku,
        title: comparisonSections?.rightResolved?.title,
        description: comparisonSections?.rightResolved?.description,
        bullets: comparisonSections?.rightResolved?.bullets
      };

      switch (type) {
        case 'draft':
          saveAsDraft(contentLibraryManagementRequest);
          break;
        case 'master':
          saveAsMaster(contentLibraryManagementRequest);
          break;
        case 'archive':
          archiveContent(contentLibraryManagementRequest);
          break;
      }
    },
    [contentGenId, retailerId, retailerSku, comparisonSections?.rightResolved, saveAsDraft, saveAsMaster, archiveContent]
  );

  const handleExport = useCallback(() => {
    const versionId =
      rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master' ? rightSelectedVersion : '';

    if (!versionId) {
      showSnackbar({ message: 'No generated content to export.', type: 'error' });
      return;
    }

    exportContent({
      retailerId: Number(retailerId),
      skuContentGenerationIds: [versionId]
    });
  }, [rightSelectedVersion, retailerId, exportContent, showSnackbar]);

  // Handlers for version selection
  const handleLeftVersionChange = useCallback((newVersionId: string) => {
    setLeftSelectedVersion(newVersionId);
  }, []);

  const handleRightVersionChange = useCallback((newVersionId: string) => {
    setRightSelectedVersion(newVersionId);
  }, []);

  const hasGeneratedContent = !error && rightSelectedVersion !== 'current' && rightSelectedVersion !== 'master';

  // Get display names
  const leftVersionName = useMemo(() => {
    const option = allVersionOptions.find((opt) => opt.id === leftSelectedVersion);
    return option?.label || 'Current Content';
  }, [leftSelectedVersion, allVersionOptions]);

  const rightVersionName = useMemo(() => {
    const option = allVersionOptions.find((opt) => opt.id === rightSelectedVersion);
    return option?.label || 'Current Content';
  }, [rightSelectedVersion, allVersionOptions]);

  // Build dynamic version details using current/master keys or the selected version
  const buildVersionDetails = useCallback(
    (selectionId: string, isLeft: boolean) => {
      if (selectionId === 'current') {
        return {
          source: 'Amazon',
          status: 'Live on retailer site',
          date: formatDateForDisplay(
            isLeft ? comparisonSections?.leftResolved?.timestamp : comparisonSections?.rightResolved?.timestamp
          )
        };
      }

      if (selectionId === 'master') {
        const formatted = formatDateForDisplay(
          libraryRecord?.masterContent?.timestamp,
          libraryRecord?.masterContent?.updatedAt
        );
        return {
          source: 'Uploaded',
          status: 'Master',
          ...(formatted ? { date: formatted } : {})
        };
      }

      const version = libraryRecord?.versions?.find((v) => v.skuContentGenerationId === selectionId);
      const normalizedStatus = version?.libraryStatus
        ? `${version.libraryStatus?.slice(0, 1).toUpperCase()}${version.libraryStatus?.slice(1).toLowerCase()}`
        : null;

      return {
        project: version?.projectName || null,
        profile: version?.profileName || null,
        profileId: version?.profileId || null,
        status: normalizedStatus
      };
    },
    [libraryRecord, comparisonSections]
  );

  const leftVersionDetails = useMemo(
    () => buildVersionDetails(leftSelectedVersion, true),
    [buildVersionDetails, leftSelectedVersion]
  );
  const rightVersionDetails = useMemo(
    () => buildVersionDetails(rightSelectedVersion, false),
    [buildVersionDetails, rightSelectedVersion]
  );

  return {
    // Data
    leftSections,
    rightSections,
    profile,
    leftMetrics,
    rightMetrics,
    hasRightContent: hasGeneratedContent,
    availableVersions,
    leftComparisonOptions,
    leftSelectedVersion,
    rightSelectedVersion,
    leftVersionName,
    rightVersionName,
    leftVersionDetails,
    rightVersionDetails,
    libraryReviewQueryKey,

    // Loading states
    isLoadingLibrary,
    isLoadingCompare,
    isLoadingLeft,
    isLoadingRight,
    isSaving,
    isExporting,

    // Paired metric loading states - both sides of a metric load together
    metricLoadingStates,

    // Error
    error,

    // Handlers
    handleBack,
    handleUpdateContentLibrary,
    handleExport,
    handleLeftVersionChange,
    handleRightVersionChange
  };
};
